import * as PIXI from "pixi.js";
import fit from "math-fit";
import gsap from "gsap";
import disp from "./displacement.jpg";
import adamTen from "./ADAM_TEN.jpg";
import bobMoses from "./Bob_Moses.jpg";
import bonobo from "./BONOBO.jpg";
import dimension from "./Dimension.jpg";
import fatBoySlim from "./Fatboy_Slim.jpg";
import ferryCorsten from "./FERRY_CORSTEN.jpg";
import interplanetaryCriminal from "./INTERPLANETARY_CRIMINAL.jpg";
import johnNewman from "./JOHN_NEWMAN.jpg";
import kellyLeeOwens from "./KELLY_LEE_OWENS.jpg";
import klangkarussell from "./KLANGKARUSSELL.jpg";
import koven from "./KOVEN.jpg";
import kream from "./KREAM.jpg";
import lpGiobbi from "./LP_GIOBBI.jpg";
import marsh from "./MARSH.jpg";
import monroe from "./Monrroe_and_Duskee.jpg";
import morten from "./MORTEN.jpg";
import mph from "./MPH.jpg";
import nilsHoffmann from "./NILS_HOFFMANN.jpg";
import omriSmadar from "./OMRI_SMADAR.jpg";
import paulVanDyke from "./Paul_Van_Dyk.jpg";
import pythius from "./Pythius.jpg";
import rodriguezJr from "./RODRIGUEZ_JR.jpg";
import swimmingPaul from "./Swimming_Paul.jpg";

// -------- utils ----------
const loadImages = (paths, whenLoaded) => {
  const imgs = [];
  const imgO = [];
  paths.forEach((path) => {
    const img = new Image();
    img.onload = () => {
      imgs.push(img);
      imgO.push({ path, img });
      if (imgs.length === paths.length) {
        whenLoaded(imgO);
      }
    };
    img.src = path;
  });
};

// --- Wave filter (sinus-based horizontal ripple) ---
const makeWaveFilter = () => {
  const vertex = `
    attribute vec2 aVertexPosition;
    attribute vec2 aTextureCoord;
    uniform mat3 projectionMatrix;
    varying vec2 vTextureCoord;
    void main(void){
      vTextureCoord = aTextureCoord;
      gl_Position = vec4((projectionMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
    }
  `;
  const fragment = `
    precision mediump float;
    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;

    uniform float uTime;
    uniform float uAmplitude;
    uniform float uFrequency;
    uniform float uSpeed;
    uniform float uStrength;

    void main(void){
      float phase = (vTextureCoord.y * uFrequency + uTime * uSpeed) * 6.2831853;
      float offsetX = sin(phase) * uAmplitude * uStrength;

      float phase2 = (vTextureCoord.y * (uFrequency * 0.5) - uTime * (uSpeed * 0.6)) * 6.2831853;
      offsetX += sin(phase2) * (uAmplitude * 0.5) * uStrength;

      vec2 uv = vec2(vTextureCoord.x + offsetX, vTextureCoord.y);

      // clamp to avoid edge artifacts while dragging
      uv = clamp(uv, vec2(0.001), vec2(0.999));

      gl_FragColor = texture2D(uSampler, uv);
    }
  `;
  const uniforms = {
    uTime: 0,
    uAmplitude: 0.02,
    uFrequency: 2.0,
    uSpeed: 0.25,
    uStrength: 0.0,
  };
  return new PIXI.Filter(vertex, fragment, uniforms);
};

class Sketch {
  constructor() {
    const ww = window.innerWidth;
    const height = ww > 1024 ? 650 : ww > 640 ? 600 : 400;

    this.app = new PIXI.Application({
      backgroundAlpha: 0,
      width: window.innerWidth, // full width
      height: height, // fixed height in px
      antialias: true,
    });

    const containerEl = document.getElementById("js-slider-container");
    if (containerEl) {
      containerEl.appendChild(this.app.view);
    } else {
      document.body.appendChild(this.app.view);
    }

    // --- configurable ---
    this.margin = 24;
    this.breakpoints = [
      { min: 1024, cols: 3, height: 650 },
      { min: 640, cols: 2, height: 600 },
      { min: 0, cols: 1.5, height: 500 },
    ];

    // state
    this.scroll = 0;
    this.scrollTarget = 0;

    // responsive columns
    this.cols = this.getColumns();
    this.width = this.calcWidthForCols(this.cols);
    this.height = this.app.renderer.height * 0.8;

    this.container = new PIXI.Container();
    this.app.stage.addChild(this.container);

    this.images = [
      adamTen,
      bobMoses,
      bonobo,
      dimension,
      fatBoySlim,
      ferryCorsten,
      interplanetaryCriminal,
      johnNewman,
      kellyLeeOwens,
      klangkarussell,
      koven,
      kream,
      lpGiobbi,
      marsh,
      monroe,
      morten,
      mph,
      nilsHoffmann,
      omriSmadar,
      paulVanDyke,
      pythius,
      rodriguezJr,
      swimmingPaul,
    ];

    this.captions = [
      "Adam Ten",
      "Bob Moses",
      "Bonobo",
      "Dimension",
      "Fatboy Slim",
      "Ferry Corsten",
      "Interplanetary Criminal",
      "John Newman",
      "Kelly Lee Owens",
      "Klangkarussell",
      "Koven",
      "KREAM",
      "LP Giobbi",
      "Marsh",
      "Monrroe & Duskee",
      "Morten",
      "MPH",
      "Nils Hoffmann",
      "Omri Smadar",
      "Paul van Dyk",
      "Pythius",
      "Rodriguez Jr",
      "Swimming Paul",
    ];

    this.WHOLEWIDTH = this.images.length * (this.width + this.margin);

    loadImages(this.images, (images) => {
      const byPathIndex = (a, b) => {
        return this.images.indexOf(a.path) - this.images.indexOf(b.path);
      };
      const ordered = images.slice().sort(byPathIndex);

      this.loadedImages = ordered;
      this.add();
      this.addFilter();
      this.render();
      this.scrollEvent();
      this.addDrag();
      this.bindResize(); // <-- start listening after everything exists
    });
  }

  // ---------- responsive helpers ----------
  getColumns = () => {
    const w = window.innerWidth;
    for (const bp of this.breakpoints) {
      if (w >= bp.min) {
        return bp.cols;
      }
    }
    return 1;
  };

  getHeightForCols = (cols) => {
    for (const bp of this.breakpoints) {
      if (cols === bp.cols) {
        return bp.height;
      }
    }
    return 650;
  };

  calcWidthForCols = (cols) => {
    // Ensures: cols*width + (cols-1)*margin === viewport width
    const W = window.innerWidth;
    return (W - (cols - 1) * this.margin) / cols;
  };

  bindResize = () => {
    window.addEventListener("resize", this.onResize, { passive: true });
  };

  onResize = () => {
    const nextCols = this.getColumns();
    if (nextCols !== this.cols) {
      this.cols = nextCols;
    }

    this.width = this.calcWidthForCols(this.cols);
    // keep canvas full width, fixed height
    this.app.renderer.resize(window.innerWidth, this.app.renderer.height);

    // update world metrics
    this.WHOLEWIDTH = this.images.length * (this.width + this.margin);

    // update filter safe area
    if (this.container) {
      this.container.filterArea = new PIXI.Rectangle(
        -64,
        -64,
        window.innerWidth + 128,
        window.innerHeight + 128
      );
    }

    // update displacement sprite coverage
    if (this.displacementSprite) {
      const target = { w: 512, h: 512 };
      const parent = { w: window.innerWidth, h: window.innerHeight };
      const cover = fit(target, parent);
      this.displacementSprite.position.set(cover.left, cover.top);
      this.displacementSprite.scale.set(cover.scale, cover.scale);
    }

    // reflow thumbs (mask, label, sprite cover, x positions)
    if (Array.isArray(this.thumbs) && this.thumbs.length > 0) {
      const parent = { w: this.width, h: this.height };

      this.thumbs.forEach((th, index) => {
        // reset base position for looping calc
        th.x = (this.margin + this.width) * index;
        th.y = this.height / 10;

        // mask
        if (th._mask) {
          th._mask.width = this.width;
          th._mask.height = this.height;
        }

        // label (wrap + bg)
        if (th._labelContainer) {
          const labelContainer = th._labelContainer;
          const bg = labelContainer.getChildAt(0); // Graphics
          const label = labelContainer.getChildAt(1); // PIXI.Text
          if (label && label.style) {
            label.style.wordWrapWidth = this.width * 0.9;
            // force metrics refresh by touching text
            label.text = label.text;
          }
          if (bg && typeof bg.clear === "function") {
            const padding = 12;
            bg.clear();
            bg.lineStyle(1, 0xffffff, 1);
            bg.beginFill(0x000000, 1);
            bg.drawRoundedRect(
              -padding / 2,
              -label.height - padding / 2,
              label.width + padding,
              label.height + padding,
              0
            );
            bg.endFill();
          }
          labelContainer.position.set(16, this.height - 16);
        }

        // image cover fit
        if (th._sprite && th._spriteContainer) {
          const sprite = th._sprite;
          const spriteContainer = th._spriteContainer;
          const image = {
            w: sprite.texture.orig.width,
            h: sprite.texture.orig.height,
          };
          const cover = fit(image, parent);
          spriteContainer.position.set(cover.left, cover.top);
          spriteContainer.scale.set(cover.scale, cover.scale);
        }
      });
    }
  };

  scrollEvent = () => {
    document.addEventListener(
      "mousewheel",
      (event) => {
        this.scrollTarget += event.wheelDelta / 5;
      },
      { passive: true }
    );
  };

  addDrag = () => {
    const view = this.app.view;
    this.isDragging = false;
    this.velocity = 0;
    this.maxSpeed = 20;

    view.addEventListener("pointerdown", (e) => {
      this.isDragging = true;
      this.startX = e.clientX;
      this.lastX = e.clientX;
      this.velocity = 0;
      this.scrollTarget = this.scroll;
    });

    view.addEventListener("pointermove", (e) => {
      if (this.isDragging) {
        let delta = e.clientX - this.lastX;
        if (delta > this.maxSpeed) {
          delta = this.maxSpeed;
        }
        if (delta < -this.maxSpeed) {
          delta = -this.maxSpeed;
        }
        this.scrollTarget += delta;
        this.lastX = e.clientX;
        this.velocity = delta;
      }
    });

    const stopDrag = () => {
      if (this.isDragging) {
        this.isDragging = false;
        let momentum = this.velocity * 10;
        if (momentum > this.maxSpeed * 10) {
          momentum = this.maxSpeed * 10;
        }
        if (momentum < -this.maxSpeed * 10) {
          momentum = -this.maxSpeed * 10;
        }
        this.scrollTarget += momentum;
      }
    };

    view.addEventListener("pointerup", stopDrag);
    view.addEventListener("pointerleave", stopDrag);
  };

  addFilter = () => {
    // Displacement
    this.displacementSprite = PIXI.Sprite.from(disp);
    this.app.stage.addChild(this.displacementSprite);

    const target = { w: 512, h: 512 };
    const parent = { w: window.innerWidth, h: window.innerHeight };
    const cover = fit(target, parent);

    this.displacementSprite.position.set(cover.left, cover.top);
    this.displacementSprite.scale.set(cover.scale, cover.scale);

    this.displacementFilter = new PIXI.filters.DisplacementFilter(
      this.displacementSprite
    );
    this.displacementFilter.x = 0;
    this.displacementFilter.y = 0;

    // Wave
    this.waveFilter = makeWaveFilter();
    this.waveStrength = 0;

    // Give warps room to breathe
    this.waveFilter.padding = 64;
    this.displacementFilter.padding = 64;

    // Prevent cropping at screen edges
    this.container.filterArea = new PIXI.Rectangle(
      -64,
      -64,
      window.innerWidth + 128,
      window.innerHeight + 128
    );

    // Filter stack
    this.container.filters = [this.waveFilter, this.displacementFilter];
  };

  add = () => {
    const parent = { w: this.width, h: this.height };
    this.thumbs = [];

    this.loadedImages.forEach(async (img, index) => {
      const texture = await PIXI.Texture.fromURL(img.path);
      const sprite = new PIXI.Sprite(texture);
      const container = new PIXI.Container();
      const spriteContainer = new PIXI.Container();

      const mask = new PIXI.Sprite(PIXI.Texture.WHITE);
      mask.width = this.width;
      mask.height = this.height;

      sprite.anchor.set(0.5);
      sprite.position.set(
        sprite.texture.orig.width / 2,
        sprite.texture.orig.height / 2
      );

      const image = {
        w: sprite.texture.orig.width,
        h: sprite.texture.orig.height,
      };
      const cover = fit(image, parent);

      spriteContainer.position.set(cover.left, cover.top);
      spriteContainer.scale.set(cover.scale, cover.scale);

      container.x = (this.margin + this.width) * index;
      container.y = this.height / 10;
      container.interactive = true;

      spriteContainer.addChild(sprite);
      container.addChild(spriteContainer);

      // ----- label + black background + white border -----
      const labelText = this.captions[index] ?? `Sample ${index + 1}`;
      const label = new PIXI.Text(
        labelText,
        new PIXI.TextStyle({
          fontFamily: "Onest, sans-serif",
          fontSize: 22,
          fontWeight: "700",
          fill: 0xffffff,
          align: "left",
          wordWrap: true,
          wordWrapWidth: this.width * 0.9,
        })
      );

      label.anchor.set(0, 1); // left-bottom
      label.position.set(0, 0);

      const padding = 12;
      const bg = new PIXI.Graphics();
      bg.lineStyle(1, 0xffffff, 1); // white stroke
      bg.beginFill(0x000000, 1); // black bg
      bg.drawRoundedRect(
        -padding / 2,
        -label.height - padding / 2,
        label.width + padding,
        label.height + padding,
        0
      );
      bg.endFill();

      const labelContainer = new PIXI.Container();
      labelContainer.addChild(bg);
      labelContainer.addChild(label);
      labelContainer.position.set(16, this.height - 16);

      container.addChild(labelContainer);

      container.on("mouseover", this.mouseOn);
      container.on("mouseout", this.mouseOut);

      container.mask = mask;
      container.addChild(mask);

      // keep refs for responsive reflow
      container._mask = mask;
      container._spriteContainer = spriteContainer;
      container._sprite = sprite;
      container._labelContainer = labelContainer;

      this.container.addChild(container);
      this.thumbs.push(container);
    });
  };

  mouseOn = (event) => {
    const imgSprite = event.currentTarget.getChildAt(0).getChildAt(0);
    gsap.to(imgSprite.scale, { duration: 1, x: 1.1, y: 1.1 });
  };

  mouseOut = (event) => {
    const imgSprite = event.currentTarget.getChildAt(0).getChildAt(0);
    gsap.to(imgSprite.scale, { duration: 1, x: 1, y: 1 });
  };

  calcPos = (scr, pos) => {
    return (
      ((scr + pos + this.WHOLEWIDTH + this.width + this.margin) %
        this.WHOLEWIDTH) -
      this.width -
      this.margin
    );
  };

  render = () => {
    this.app.ticker.add((delta) => {
      this.app.renderer.render(this.container);

      this.direction = this.scroll > 0 ? -1 : 1;

      const maxSpeed = 20;
      let diff = this.scrollTarget - this.scroll;
      if (diff > maxSpeed) {
        diff = maxSpeed;
      }
      if (diff < -maxSpeed) {
        diff = -maxSpeed;
      }

      this.scroll += diff * 0.1;
      this.scroll *= 0.9;
      this.scrollTarget *= 0.95;

      this.thumbs.forEach((th) => {
        th.position.x = this.calcPos(this.scroll, th.position.x);
      });

      // displacement reacts to overall motion
      this.displacementFilter.scale.x =
        3 * this.direction * Math.abs(this.scroll);

      // wave strength follows instant speed with damping
      const speed = Math.abs(diff);
      const targetStrength = Math.min(speed / maxSpeed, 1);
      this.waveStrength += (targetStrength - this.waveStrength) * 0.15;
      this.waveStrength *= 0.684;

      if (this.waveFilter) {
        this.waveFilter.uniforms.uTime += (delta || 1) / 60.0;
        this.waveFilter.uniforms.uStrength = this.waveStrength;

        // soften amplitude a bit at high strength to avoid edge pull
        const baseAmp = 0.016;
        const ampDampen = 1.0 - this.waveStrength * 0.25;
        this.waveFilter.uniforms.uAmplitude = baseAmp * ampDampen;

        this.waveFilter.uniforms.uFrequency = 2.0 + this.waveStrength * 1.25;
      }
    });
  };
}

new Sketch();
